import { useState, useEffect, useCallback } from 'react';
import { X, Mail, Smartphone, QrCode, Loader2, Monitor, PenTool, Share2, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { CardSelectInput } from '@/Components/shared/CardSelectInput';
import { FormInput } from '@/Components/shared/FormInput';
import { PhoneInput } from '@/Components/shared/PhoneInput';
import { DirectPaymentModal } from '@/features/contracts/components/DirectPaymentModal';

interface PaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (email: string, phone: string, signatureType: 1 | 2, skipNotifications: boolean, paymentExpirationHours: number) => void;
  defaultEmail: string;
  defaultPhone: string;
  defaultSignatureType: 1 | 2;
  beneficiaryName: string;
  isSubmitting: boolean;
  /** After contract creation, the payment link is available */
  paymentLink?: string;
  /** After contract creation with electronic signature, the YouSign link
   * (which redirects to paymentLink after signature) */
  signatureLink?: string;
  /** Context to adapt wording: 'monthly' for SetupIntent, 'online' for PaymentIntent */
  paymentContext?: 'monthly' | 'online';
  /** Default expiration hours from society configuration */
  defaultExpirationHours?: number;
  warrantyType?: number;
  guarantorName?: string;
}

type ShareMode = 'email-sms' | 'qrcode' | 'direct';

/**
 * Modale "Partage du lien de paiement"
 * Affichée avant la création d'un contrat avec paiement Stripe (mensuel ou comptant en ligne).
 *
 * Phase 1 (avant création) : permet de modifier email/téléphone du bénéficiaire
 * Phase 2 (après création) : affiche le QR code OU délègue au DirectPaymentModal (mode direct)
 */
export function PaymentLinkModal({
  isOpen,
  onClose,
  onConfirm,
  defaultEmail,
  defaultPhone,
  defaultSignatureType,
  isSubmitting,
  paymentLink,
  signatureLink,
  paymentContext = 'monthly',
  defaultExpirationHours = 24,
  warrantyType,
  guarantorName,
}: PaymentLinkModalProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [signatureType, setSignatureType] = useState<1 | 2>(defaultSignatureType);
  const [shareMode, setShareMode] = useState<ShareMode>('email-sms');
  const [expirationHours, setExpirationHours] = useState<number>(defaultExpirationHours);

  if (!isOpen) return null;

  const isPostCreation = !!paymentLink;
  const isDirectMode = shareMode === 'direct';
  // QR code et paiement direct ne déclenchent aucun envoi automatique :
  // dans les deux cas, le commercial partage le lien lui-même au bénéficiaire.
  const skipNotifications = shareMode !== 'email-sms';

  // Reset shareMode if switching from manuscrite to électronique while in direct mode
  useEffect(() => {
    if (signatureType === 1 && shareMode === 'direct') {
      setShareMode('email-sms');
    }
  }, [signatureType, shareMode]);

  const handleConfirm = () => {
    onConfirm(email, phone, signatureType, skipNotifications, expirationHours);
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Phase 2 en mode direct : déléguer à DirectPaymentModal
  if (isPostCreation && isDirectMode && paymentLink) {
    return (
      <DirectPaymentModal
        isOpen
        onClose={onClose}
        paymentLink={paymentLink}
        onPaymentSuccess={() => {
          // Le DirectPaymentModal gère le completePayment en interne
        }}
        successMessage="Contrat créé avec succès ! Procédez au paiement ci-dessous."
      />
    );
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <Mail className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              {paymentContext === 'online'
                ? 'Paiement comptant en ligne'
                : 'Paiement mensualisé'
              }
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="tw-p-6 tw-space-y-6">
          {!isPostCreation ? (
            <>
              {/* Signature type selector */}
              <div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                  <PenTool className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
                  <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Type de signature</h4>
                </div>
                <CardSelectInput
                  options={[
                    { value: '1', label: 'Électronique', description: 'Signature en ligne par le client', icon: <Monitor className="tw-w-5 tw-h-5" /> },
                    { value: '2', label: 'Manuscrite', description: 'Signature papier physique', icon: <PenTool className="tw-w-5 tw-h-5" /> },
                  ]}
                  value={signatureType.toString()}
                  onChange={(val) => setSignatureType(parseInt(val, 10) as 1 | 2)}
                  name="signature-type"
                  required
                  columns={2}
                />
              </div>

              {/* Mode selector */}
              <div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                  <Share2 className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
                  <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Partage du lien de paiement</h4>
                </div>
                <div className="tw-flex tw-gap-2">
                  <button
                    type="button"
                    onClick={() => setShareMode('email-sms')}
                    className={`tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-transition-colors tw-text-sm tw-font-medium ${
                      shareMode === 'email-sms'
                        ? 'tw-border-affinicar-primary tw-bg-affinicar-lighter tw-text-affinicar-primary'
                        : 'tw-border-gray-200 tw-text-gray-600 hover:tw-border-gray-300'
                    }`}
                  >
                    <Smartphone className="tw-w-4 tw-h-4" />
                    Email & SMS
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareMode('qrcode')}
                    className={`tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-transition-colors tw-text-sm tw-font-medium ${
                      shareMode === 'qrcode'
                        ? 'tw-border-affinicar-primary tw-bg-affinicar-lighter tw-text-affinicar-primary'
                        : 'tw-border-gray-200 tw-text-gray-600 hover:tw-border-gray-300'
                    }`}
                  >
                    <QrCode className="tw-w-4 tw-h-4" />
                    QR Code
                  </button>
                  {signatureType === 2 && (
                    <button
                      type="button"
                      onClick={() => setShareMode('direct')}
                      className={`tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-transition-colors tw-text-sm tw-font-medium ${
                        shareMode === 'direct'
                          ? 'tw-border-affinicar-primary tw-bg-affinicar-lighter tw-text-affinicar-primary'
                          : 'tw-border-gray-200 tw-text-gray-600 hover:tw-border-gray-300'
                      }`}
                    >
                      <CreditCard className="tw-w-4 tw-h-4" />
                      Paiement en direct
                    </button>
                  )}
                </div>
              </div>

              {shareMode === 'email-sms' ? (
                <div className="tw-space-y-4">
                  <p className="tw-text-sm tw-text-gray-600">
                    {signatureType === 1
                      ? paymentContext === 'online'
                        ? 'Un lien de paiement et de signature électronique sera envoyé par email et SMS.'
                        : 'Le lien de paiement et de signature électronique sera automatiquement envoyé par email et SMS aux coordonnées ci-dessous. Vous pouvez modifier les coordonnées si nécessaire.'
                      : paymentContext === 'online'
                        ? 'Un lien de paiement comptant sera envoyé par email et SMS.'
                        : 'Le lien de paiement sera automatiquement envoyé par email et SMS aux coordonnées ci-dessous. Vous pouvez les modifier si nécessaire.'
                    }
                    <br />
                    {signatureType === 1
                      ? 'Le bénéficiaire pourra régler et signer le contrat en ligne dans un délai de '
                      : 'Le bénéficiaire pourra régler le contrat en ligne dans un délai de '
                    }
                    <select
                      value={expirationHours}
                      onChange={(e) => setExpirationHours(Number(e.target.value))}
                      disabled={isSubmitting}
                      className="tw-inline tw-px-2 tw-py-0.5 tw-text-sm tw-font-medium tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary tw-bg-white disabled:tw-opacity-50"
                    >
                      <option value={1}>1 heure</option>
                      <option value={12}>12 heures</option>
                      <option value={24}>24 heures</option>
                      <option value={48}>48 heures</option>
                      <option value={72}>72 heures</option>
                    </select>.
                  </p>

                  <FormInput
                    type="email"
                    label="Email du bénéficiaire"
                    value={email}
                    onChange={setEmail}
                    placeholder="email@exemple.fr"
                    required
                  />

                  <PhoneInput
                    label="Téléphone du bénéficiaire"
                    value={phone}
                    onChange={setPhone}
                    required
                  />
                </div>
              ) : shareMode === 'qrcode' ? (
                <div className="tw-text-center tw-space-y-3">
                  <p className="tw-text-sm tw-text-gray-600">
                    Le QR Code sera affiché après la création du contrat. Le bénéficiaire pourra le scanner pour accéder au lien de paiement.
                  </p>
                  <div className="tw-bg-gray-100 tw-rounded-lg tw-p-8 tw-flex tw-items-center tw-justify-center">
                    <div className="tw-text-gray-400 tw-text-center">
                      <QrCode className="tw-w-16 tw-h-16 tw-mx-auto tw-mb-2" />
                      <p className="tw-text-sm">Disponible après création</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tw-text-center tw-space-y-3">
                  <p className="tw-text-sm tw-text-gray-600">
                    Le formulaire de paiement sera affiché après la création du contrat. Vous pourrez procéder au règlement directement depuis cet écran.
                  </p>
                  <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-text-blue-800 tw-mb-0">
                      Aucune notification ne sera envoyée au bénéficiaire. Le paiement sera effectué en direct.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Post-creation: show QR code */
            (() => {
              // Si signature électronique : le QR encode le lien YouSign
              // (qui redirige vers le paiement après signature).
              // Sinon : QR sur le lien de paiement direct.
              const useSignatureLink = signatureType === 1 && !!signatureLink;
              const qrCodeValue = (useSignatureLink ? signatureLink : paymentLink) ?? '';
              const electronicWithoutLink = signatureType === 1 && !signatureLink;
              const isQrCodeShare = shareMode === 'qrcode';

              return (
                <div className="tw-text-center tw-space-y-4">
                  <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-text-green-800 tw-font-medium tw-mb-0">
                      {isQrCodeShare
                        ? 'Contrat créé avec succès ! Faites scanner le QR Code ci-dessous au bénéficiaire — aucun email ni SMS n\'a été envoyé.'
                        : useSignatureLink
                          ? 'Contrat créé avec succès ! Le bénéficiaire va recevoir le lien de signature et de paiement par email et SMS dans quelques instants.'
                          : 'Contrat créé avec succès ! Le bénéficiaire va recevoir le lien de paiement par email et SMS dans quelques instants.'}
                    </p>
                  </div>

                  {electronicWithoutLink && (
                    <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4">
                      <p className="tw-text-sm tw-text-orange-800 tw-font-medium tw-mb-0">
                        La signature électronique n'a pas pu être préparée. Le QR Code ci-dessous mène directement au paiement — vous pourrez relancer la signature depuis la fiche du contrat.
                      </p>
                    </div>
                  )}

                  <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-4">
                    <p className="tw-text-sm tw-text-yellow-800 tw-font-medium tw-mb-0">
                      Le bénéficiaire dispose de <b>{expirationHours} heure{expirationHours > 1 ? 's' : ''}</b> pour finaliser son paiement. <br />
                      Passé ce délai, le contrat sera automatiquement annulé.
                    </p>
                  </div>

                  <p className="tw-text-sm tw-text-gray-600">
                    {useSignatureLink
                      ? 'Vous pouvez aussi partager ce QR Code avec le bénéficiaire (signature électronique puis paiement) :'
                      : 'Vous pouvez aussi partager ce QR Code avec le bénéficiaire :'}
                  </p>

                  <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-inline-block tw-border tw-border-gray-200">
                    <QRCodeSVG value={qrCodeValue} size={200} />
                  </div>

                  <p className="tw-text-xs tw-text-gray-500 tw-break-all tw-px-4">
                    {qrCodeValue}
                  </p>
                </div>
              );
            })()
          )}
        </div>

        {/* Engagement légal (phase pré-création uniquement) */}
        {!isPostCreation && warrantyType !== undefined && (
          <div className="tw-px-6 tw-pb-2">
            <p className="tw-text-xs tw-text-gray-500 tw-italic tw-mb-0">
              {guarantorName ? (
                warrantyType === 1
                  ? <>En cliquant sur "Générer le contrat", j'accepte les termes du contrat et engage la société <strong>{guarantorName}</strong> à prendre en charge les prestations de maintenance corrective.</>
                  : <>En cliquant sur "Générer le contrat", j'accepte les termes du contrat et engage la société <strong>{guarantorName}</strong> en tant que garant.</>
              ) : (
                warrantyType === 1
                  ? 'En cliquant sur "Générer le contrat", j\'accepte les termes du contrat et engage ma société à prendre en charge les prestations de maintenance corrective.'
                  : 'En cliquant sur "Générer le contrat", j\'accepte les termes du contrat et engage ma société en tant que garant.'
              )}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
          {!isPostCreation ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting || (shareMode !== 'direct' && !email)}
                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  'Confirmer et créer le contrat'
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleClose}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
